import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Vote, Clock, CheckCircle2, AlertCircle, Award, ShieldCheck } from "lucide-react";
import { Badge } from "../components/ui/badge";

/**
 * Voter-facing Elections list. Renders every election the caller is allowed
 * to see: active, upcoming, closed under review, and certified. Delegates
 * the actual ballot flow to /elections/:id.
 */
export default function Elections() {
    const [items, setItems] = useState(null);
    const [err, setErr] = useState("");

    useEffect(() => {
        api.get("/elections")
            .then(({ data }) => setItems(data || []))
            .catch((e) => setErr(formatApiError(e?.response?.data?.detail) || "Could not load elections."));
    }, []);

    if (err) return <div className="max-w-3xl mx-auto px-6 py-12 text-destructive">{err}</div>;
    if (items === null) return <div className="max-w-3xl mx-auto px-6 py-12 text-muted-foreground">Loading elections…</div>;

    const active = items.filter((e) => ["open", "runoff_open"].includes(e.status));
    const upcoming = items.filter((e) => ["scheduled"].includes(e.status));
    const closed = items.filter((e) => ["closed", "under_review", "runoff_required", "runoff_closed"].includes(e.status));
    const certified = items.filter((e) => e.status === "certified");

    return (
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-12" data-testid="elections-page">
            <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-2xl bg-primary/15 flex items-center justify-center">
                    <Vote className="h-6 w-6 text-primary" />
                </div>
                <div>
                    <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight">Elections</h1>
                    <p className="text-muted-foreground mt-1">Cast your ballot, review runoffs, and see certified results.</p>
                </div>
            </div>

            {active.length === 0 && upcoming.length === 0 && closed.length === 0 && certified.length === 0 && (
                <div className="mt-10 p-8 rounded-2xl border border-dashed text-center text-muted-foreground" data-testid="elections-empty">
                    No elections are available right now.
                </div>
            )}

            {active.length > 0 && (
                <Section title="Voting now open" icon={<Vote className="h-4 w-4 text-primary" />}>
                    {active.map((e) => <ElectionCard key={e.id} e={e} />)}
                </Section>
            )}
            {upcoming.length > 0 && (
                <Section title="Upcoming" icon={<Clock className="h-4 w-4 text-muted-foreground" />}>
                    {upcoming.map((e) => <ElectionCard key={e.id} e={e} />)}
                </Section>
            )}
            {closed.length > 0 && (
                <Section title="Awaiting certification" icon={<AlertCircle className="h-4 w-4 text-amber-500" />}>
                    {closed.map((e) => <ElectionCard key={e.id} e={e} />)}
                </Section>
            )}
            {certified.length > 0 && (
                <Section title="Certified results" icon={<Award className="h-4 w-4 text-emerald-600" />}>
                    {certified.map((e) => <ElectionCard key={e.id} e={e} />)}
                </Section>
            )}
        </div>
    );
}

function Section({ title, icon, children }) {
    return (
        <section className="mt-10">
            <div className="flex items-center gap-2 mb-4">
                {icon}
                <h2 className="text-lg font-semibold uppercase tracking-wider text-muted-foreground">{title}</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>
        </section>
    );
}

function statusBadge(s) {
    const map = {
        draft: { label: "Draft", cls: "bg-slate-100 text-slate-700" },
        scheduled: { label: "Scheduled", cls: "bg-blue-100 text-blue-700" },
        open: { label: "Voting Open", cls: "bg-emerald-100 text-emerald-800" },
        closed: { label: "Closed", cls: "bg-amber-100 text-amber-800" },
        under_review: { label: "Under Review", cls: "bg-amber-100 text-amber-800" },
        runoff_required: { label: "Runoff Required", cls: "bg-orange-100 text-orange-800" },
        runoff_open: { label: "Runoff Voting", cls: "bg-emerald-100 text-emerald-800" },
        runoff_closed: { label: "Runoff Closed", cls: "bg-amber-100 text-amber-800" },
        certified: { label: "Certified", cls: "bg-primary/15 text-primary" },
    };
    return map[s] || { label: s, cls: "bg-slate-100 text-slate-700" };
}

function ElectionCard({ e }) {
    const b = statusBadge(e.status);
    return (
        <Link
            to={`/elections/${e.id}`}
            className="group block rounded-2xl border border-slate-200 bg-white hover:border-primary/40 hover:shadow-lg transition-all p-5"
            data-testid={`election-card-${e.id}`}
        >
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="font-heading text-lg font-semibold group-hover:text-primary truncate">{e.name}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{e.election_type === "runoff" ? "Runoff Election" : "General Election"}</div>
                </div>
                <Badge className={`${b.cls} rounded-full font-semibold text-[10px] uppercase tracking-wider`}>{b.label}</Badge>
            </div>
            {e.description && <p className="text-sm text-muted-foreground mt-3 line-clamp-2">{e.description}</p>}
            <div className="mt-3 flex flex-wrap gap-3 text-xs text-muted-foreground">
                <span>{new Date(e.starts_at).toLocaleDateString()} → {new Date(e.closes_at).toLocaleDateString()}</span>
                <span>· {e.offices?.length || 0} office{(e.offices?.length || 0) === 1 ? "" : "s"}</span>
            </div>
            <div className="mt-4 flex items-center gap-2 text-xs">
                {e.has_voted && (
                    <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold" data-testid={`election-voted-${e.id}`}>
                        <CheckCircle2 className="h-3.5 w-3.5" /> Ballot submitted
                    </span>
                )}
                {!e.eligible && (
                    <span className="inline-flex items-center gap-1 text-amber-700">
                        <ShieldCheck className="h-3.5 w-3.5" /> Not eligible
                    </span>
                )}
            </div>
        </Link>
    );
}
